(() => {
  const existingController = window.PromoCollectionBanner;

  if (existingController) {
    existingController.init(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const capsules = new Set();
  let scrollFrame = 0;
  let capsuleListenersActive = false;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const updateCapsules = () => {
    scrollFrame = 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    capsules.forEach((capsule) => {
      if (!capsule.isConnected) {
        capsules.delete(capsule);
        return;
      }

      const section = capsule.closest('[data-promo-collection]');
      if (!section) return;

      const rect = section.getBoundingClientRect();
      if (rect.bottom < -250 || rect.top > viewportHeight + 250) return;

      const travel = viewportHeight + rect.height;
      const progress = clamp((viewportHeight - rect.top) / travel, 0, 1);
      const speed = Number.parseFloat(capsule.dataset.rotationSpeed) || 0;
      capsule.style.setProperty('--promo-capsule-rotation', `${progress * speed}deg`);
    });
  };

  const requestCapsuleUpdate = () => {
    if (scrollFrame || reducedMotion.matches || capsules.size === 0) return;
    scrollFrame = window.requestAnimationFrame(updateCapsules);
  };

  const enableCapsuleListeners = () => {
    if (capsuleListenersActive || capsules.size === 0) return;
    window.addEventListener('scroll', requestCapsuleUpdate, { passive: true });
    window.addEventListener('resize', requestCapsuleUpdate, { passive: true });
    capsuleListenersActive = true;
  };

  const disableCapsuleListeners = () => {
    if (!capsuleListenersActive || capsules.size > 0) return;
    window.removeEventListener('scroll', requestCapsuleUpdate);
    window.removeEventListener('resize', requestCapsuleUpdate);
    if (scrollFrame) {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = 0;
    }
    capsuleListenersActive = false;
  };

  const setActiveSlide = (section, index) => {
    const accents = Array.from(section.querySelectorAll('[data-promo-accent-slide]'));
    const media = Array.from(section.querySelectorAll('[data-promo-media-slide]'));
    const link = section.querySelector('[data-promo-link]');

    accents.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    media.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    if (link) {
      const activeAccent = accents[index];
      const slideLink = activeAccent?.dataset.slideLink || '';
      const slideAriaLabel = activeAccent?.dataset.slideAriaLabel || '';

      if (slideLink) {
        link.href = slideLink;
        link.classList.remove('promo-collection__link--disabled');
        link.removeAttribute('aria-disabled');
      } else {
        link.removeAttribute('href');
        link.classList.add('promo-collection__link--disabled');
        link.setAttribute('aria-disabled', 'true');
      }

      if (slideAriaLabel) link.setAttribute('aria-label', slideAriaLabel);
    }
  };

  const initSection = (section) => {
    if (section.dataset.promoInitialized === 'true') return;
    section.dataset.promoInitialized = 'true';

    const accents = section.querySelectorAll('[data-promo-accent-slide]');
    const media = section.querySelectorAll('[data-promo-media-slide]');
    const slideCount = Math.min(accents.length, media.length);
    const interval = Math.max(Number.parseInt(section.dataset.autoplayInterval, 10) || 3000, 1000);
    let activeIndex = 0;
    let timer = 0;
    let isVisible = true;
    let isPaused = false;

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = 0;
    };

    const start = () => {
      stop();
      if (slideCount < 2 || reducedMotion.matches || !isVisible || isPaused || document.hidden) return;

      timer = window.setInterval(() => {
        activeIndex = (activeIndex + 1) % slideCount;
        setActiveSlide(section, activeIndex);
      }, interval);
    };

    const pause = () => {
      isPaused = true;
      stop();
    };

    const resume = () => {
      isPaused = false;
      start();
    };

    const handleDocumentVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    const handleMotionPreference = () => {
      if (reducedMotion.matches) stop();
      else start();
    };

    const visibilityObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          isVisible = entries[0]?.isIntersecting ?? true;
          if (isVisible) start();
          else stop();
        }, { rootMargin: '150px 0px' })
      : null;

    section.addEventListener('mouseenter', pause);
    section.addEventListener('mouseleave', resume);
    section.addEventListener('focusin', pause);
    section.addEventListener('focusout', resume);
    document.addEventListener('visibilitychange', handleDocumentVisibility);
    reducedMotion.addEventListener?.('change', handleMotionPreference);

    if (visibilityObserver) visibilityObserver.observe(section);
    else start();

    section.querySelectorAll('[data-promo-capsule]').forEach((capsule) => capsules.add(capsule));
    enableCapsuleListeners();
    requestCapsuleUpdate();

    section.promoCleanup = () => {
      stop();
      visibilityObserver?.disconnect();
      section.removeEventListener('mouseenter', pause);
      section.removeEventListener('mouseleave', resume);
      section.removeEventListener('focusin', pause);
      section.removeEventListener('focusout', resume);
      document.removeEventListener('visibilitychange', handleDocumentVisibility);
      reducedMotion.removeEventListener?.('change', handleMotionPreference);
      section.querySelectorAll('[data-promo-capsule]').forEach((capsule) => capsules.delete(capsule));
      disableCapsuleListeners();
      delete section.dataset.promoInitialized;
    };
  };

  const init = (scope = document) => {
    if (scope.matches?.('[data-promo-collection]')) initSection(scope);
    scope.querySelectorAll?.('[data-promo-collection]').forEach(initSection);
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.('[data-promo-collection]')
      ? event.target
      : event.target.querySelector?.('[data-promo-collection]');
    section?.promoCleanup?.();
  });

  reducedMotion.addEventListener?.('change', () => {
    if (reducedMotion.matches) {
      capsules.forEach((capsule) => capsule.style.removeProperty('--promo-capsule-rotation'));
    } else {
      requestCapsuleUpdate();
    }
  });

  window.PromoCollectionBanner = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
